import { Button } from '@ps/isp-web-component-library';
import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { TrackingContext, inputFilled, useTrackers } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { StarsWebExtraWallet } from '@swf/core/models/sports/extraWallets/types';
import { getIsWalletBonusCash } from '@swf/core/models/sports/extraWallets/utils/getIsWallet';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';

import usePromotionPicker from '../../../../../../../../hooks/usePromotionPicker';

import {
  TXTCLI_Add,
  TXTCLI_ExpiresX_N0,
  TXTWCL_Add_promotion,
  TXTWCL_N0_Bonus_Cash,
  TXTWCL_N0_Free_Bet,
  TXTWCL_Stake
} from './Promotion.messages.json';
import styles from './Promotion.scss';

export interface PromotionProps {
  wallet: StarsWebExtraWallet;
  opportunity: SingleOpportunity | MultipleOpportunity;
  numberOfLines: number;
}

const Promotion: React.FC<PromotionProps> = ({ wallet, opportunity, numberOfLines }) => {
  const isBonusCash = getIsWalletBonusCash(wallet);
  const dispatch = useDispatch<Dispatch>();
  const { formatMessage } = useRegulatoryIntl();
  const { promotionAdded } = useTrackers();
  const tracking = useContext(TrackingContext);
  const trackingLabelFieldName = useTrackingLabel(TXTWCL_Stake);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);

  const { setPromotionFieldValue } = usePromotionPicker(
    opportunity,
    opportunity.id,
    numberOfLines,
    wallet.id
  );

  return (
    <div className={styles.promotion}>
      <div className={styles.promotionInfo}>
        <span className={styles.promotionAmount} id={`${wallet.id}-amount`}>
          <Translation
            {...(isBonusCash ? TXTWCL_N0_Bonus_Cash : TXTWCL_N0_Free_Bet)}
            values={{
              0: <CurrencyAmount value={wallet.amount} dollarValue currency={wallet.currency} />
            }}
          />
        </span>
        <span className={styles.promotionExpiry}>
          <Translation
            {...TXTCLI_ExpiresX_N0}
            values={{
              0: <FormattedDateTime date={wallet.expiry} format="short" hideTime />
            }}
          />
        </span>
      </div>

      <Button
        aria-label={formatMessage(TXTWCL_Add_promotion)}
        aria-describedby={`${wallet.id}-amount`}
        variant="brand-promotion"
        size="small"
        onClick={() => {
          setPromotionFieldValue();
          promotionAdded({ ...wallet, expiry: new Date(wallet.expiry).getTime() });
          dispatch.betSlip.promotionPickerToggled(undefined);
          dispatch.betSlip.promotionAdded(wallet.id);
          inputFilled(
            {
              category: tracking.category,
              widget: tracking.widget,
              isMobileEmbedded
            },
            {
              fieldName: trackingLabelFieldName,
              numInput: wallet.amount,
              fieldType: 'number'
            }
          );
        }}
      >
        <Translation {...TXTCLI_Add} />
      </Button>
    </div>
  );
};

export default Promotion;
